import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { api } from '../../lib/api'
import type { User } from '../../hooks/useUsers'
import type { DriverStatusBeacon } from '../../hooks/useWorkforce'

interface Props {
  drivers: User[]
  beacons: DriverStatusBeacon[]
  onClose: () => void
}

/// Wave 1 E1.2 — Broadcast Modal.
///
/// Sends a tenant-wide notification to a subset of drivers. The MVP target
/// is the existing notification service `POST /notifications/send`; the full
/// multi-channel routing lands with E1.4.
export function BroadcastModal({ drivers, beacons, onClose }: Props) {
  const { t } = useTranslation()
  const [audience, setAudience] = useState<'all' | 'on_shift'>('on_shift')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const onShiftIds = new Set(
    beacons
      .filter((b) => b.beacon !== 'offline')
      .map((b) => b.driver_user_id),
  )
  const targets = audience === 'all' ? drivers.map((d) => d.id) : drivers.filter((d) => onShiftIds.has(d.id)).map((d) => d.id)

  const onSend = async () => {
    if (!message.trim()) return
    setBusy(true)
    setError(null)
    try {
      await api.post('/notifications/send', {
        title: t('dispatch.broadcastTitle', 'Dispatch broadcast'),
        body: message.trim(),
        user_ids: targets,
        severity: 'info',
      })
      onClose()
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } }; message?: string }
      setError(err.response?.data?.error ?? err.message ?? 'send failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-slate-900/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900">
            {t('dispatch.broadcast', 'Broadcast')}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <div className="text-xs font-medium text-slate-700 mb-1">
              {t('dispatch.audience', 'Audience')}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setAudience('on_shift')}
                className={`px-3 py-1.5 rounded text-sm border ${audience === 'on_shift' ? 'bg-sky-600 text-white border-sky-600' : 'border-slate-300 text-slate-700'}`}
              >
                {t('dispatch.audience.onShift', 'On-shift')} ({drivers.filter((d) => onShiftIds.has(d.id)).length})
              </button>
              <button
                type="button"
                onClick={() => setAudience('all')}
                className={`px-3 py-1.5 rounded text-sm border ${audience === 'all' ? 'bg-sky-600 text-white border-sky-600' : 'border-slate-300 text-slate-700'}`}
              >
                {t('dispatch.audience.all', 'All drivers')} ({drivers.length})
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-slate-700 mb-1 block">
              {t('dispatch.message', 'Message')}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full border border-slate-300 rounded p-2 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
              placeholder={t('dispatch.messageHint', 'Dust storm closing route 80 — switch to alt route via highway 5.')}
            />
          </div>

          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded text-slate-700 hover:bg-slate-100">
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={onSend}
            disabled={busy || !message.trim() || targets.length === 0}
            className="px-4 py-2 text-sm rounded bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-50"
          >
            {busy ? t('common.sending', 'Sending...') : t('common.send', 'Send')}
          </button>
        </div>
      </div>
    </div>
  )
}
